import { TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { DialogType, SelectOption } from '../../../@types';
import { CasinoTransactionsFilters } from '../../../@types/casino';
import { getDefaultCasinoReportRole, prepareCasinoTransactionFilters } from '../../../helpers/casino';
import useFilterForm from '../../../hooks/useFilterForm';
import { AdditionalQueryParams } from '../../../hooks/usePagination';
import { useCasinoProviderOptions } from '../../../hooks/useCasinoProviderOptions';
import usePermissions from '../../../hooks/usePermissions';
import FilterDialogLayout from '../../layouts/FilterDialogLayout';
import FormNumberInput from '../../molecules/FormNumberInput';
import FormSelect from '../../molecules/FormSelect';
import CasinoRoleFilters from './CasinoRoleFilters';

type FilterCasinoTransactionsData = CasinoTransactionsFilters;

const DEFAULT_FORM_DATA: FilterCasinoTransactionsData = {
  roundId: undefined,
  gameName: undefined,
  transactionAmount: undefined,
  providerName: undefined,
  transactionType: undefined,
  managerIds: undefined,
  agentIds: undefined,
  playerIds: undefined,
};

const TRANSACTION_TYPE_OPTIONS: SelectOption[] = [
  { id: '', name: 'All transactions' },
  { id: 'pay_in', name: 'Pay in' },
  { id: 'pay_out', name: 'Pay out' },
];

type Props = DialogType & {
  changeQuery: (data: AdditionalQueryParams) => void;
};

const FilterCasinoTransactions = ({ changeQuery, closeModal }: Props) => {
  const { role: userRole } = usePermissions();
  const { options: providerOptions, isLoading: providersLoading } = useCasinoProviderOptions();
  const { control, handleResetFilter, handleSubmit, watch } = useFilterForm<FilterCasinoTransactionsData>({
    defaultData: {
      ...DEFAULT_FORM_DATA,
      role: getDefaultCasinoReportRole(userRole),
    },
    changeQuery,
    prepareQueryParams: prepareCasinoTransactionFilters,
  });

  return (
    <FilterDialogLayout
      label='casino transactions'
      onSave={handleSubmit}
      onClose={() => closeModal?.()}
      onReset={handleResetFilter}
    >
      <Controller
        control={control}
        name='roundId'
        render={({ field }) => <TextField {...field} value={field.value ?? ''} label='Round ID' />}
      />
      <Controller
        control={control}
        name='gameName'
        render={({ field }) => <TextField {...field} value={field.value ?? ''} label='Game name' />}
      />
      <FormNumberInput
        control={control}
        name='transactionAmount'
        label='Transaction amount'
        allowDecimals
        valueAsString
      />
      <FormSelect
        control={control}
        name='providerName'
        label='Provider'
        options={providerOptions}
        disabled={providersLoading && !providerOptions.length}
      />
      <FormSelect
        control={control}
        name='transactionType'
        label='Transaction type'
        options={TRANSACTION_TYPE_OPTIONS}
      />
      <CasinoRoleFilters control={control} watch={watch} roleName='role' />
    </FilterDialogLayout>
  );
};

export default FilterCasinoTransactions;
